import firebase from "../services/firebase";

// The `pagehide`/`beforeunload` beacon cannot await anything - the document is
// already going away - so the id token has to be available synchronously.
// Firebase refreshes it roughly hourly and fires this listener each time.
let cachedIdToken = null;
let unsubscribe = null;

export const initIdTokenCache = () => {
  if (unsubscribe) return unsubscribe;

  // `getIdToken()` is async, so callbacks can finish out of order: a lookup for
  // the user who just signed out could otherwise resolve last and put their
  // token back, leaving the close beacon holding the wrong credential. Only the
  // newest callback may write, and tearing the cache down retires them all.
  let generation = 0;

  const unsubscribeFromAuth = firebase.auth.onIdTokenChanged(async (user) => {
    const callbackGeneration = ++generation;

    // Whatever is cached belongs to the state this event supersedes, so drop it
    // now rather than at the end of the lookup: an unload in between must send
    // nothing at all rather than the previous user's credential.
    cachedIdToken = null;

    if (!user) return;

    try {
      const idToken = await user.getIdToken();
      if (callbackGeneration === generation) cachedIdToken = idToken;
    } catch (error) {
      if (callbackGeneration === generation) cachedIdToken = null;
    }
  });

  // Clearing the module state here is what lets a later init register a fresh
  // listener - otherwise it would hand back a spent unsubscribe and leave the
  // close beacon with no token at all.
  unsubscribe = () => {
    generation += 1;
    cachedIdToken = null;
    unsubscribe = null;
    unsubscribeFromAuth();
  };

  return unsubscribe;
};

export const getCachedIdToken = () => cachedIdToken;
